import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { Table, Popover, PopoverBody, Input, Button, FormGroup, Label } from "reactstrap";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Pagination from "./Pagination";
import get from "lodash/get";
import { ReactComponent as DragdropIcon } from "assets/icons/dragdrop.svg";
import TableShimmer from "./TableShimmer";
import TableMessage from "./TableMessage";
import "./TableStyles.css";

const BREAKPOINTS = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
};

const getSafeId = (type, accessor) => {
  return `${type}-${accessor.replace(/[^a-zA-Z0-9]/g, "-")}`;
};

const SortableTableRow = ({ row, index, columns, selectable, selectedRows, handleSelectRow, keyField = "id" }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: row[keyField]?.toString() || index.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr ref={setNodeRef} style={style} className={selectedRows.includes(row[keyField]) ? "selected" : ""} data-handler-id={`row-${index}`}>
      {selectable && (
        <td className="text-center">
          <div className="d-flex align-items-center gap-2">
            <div {...attributes} {...listeners} style={{ cursor: "grab" }}>
              <DragdropIcon />
            </div>
            <Input
              type="checkbox"
              checked={selectedRows.includes(row[keyField])}
              onChange={() => {}}
              onClick={() => {
                handleSelectRow(row[keyField]);
              }}
              className="form-check-input"
            />
          </div>
        </td>
      )}
      {columns.map((column, colIndex) => {
        return <td key={colIndex}>{column.render ? column.render(row) : get(row, column.accessor)}</td>;
      })}
    </tr>
  );
};

const CustomTable = ({ data, columns, maxHeight, selectable = false, onSelectionChange, selectionActions, sortable = false, onSortedChange, itemsPerPage = Number.MAX_VALUE, className = "", onRowClick, onSort, onFilter, onSearch, totalItems, onPageChange, displayFooter = true, initSelectedRows = [], keyField = "id", status = "normal", errorMessage = "เกิดข้อผิดพลาดในการโหลดข้อมูล กรุณาลองใหม่อีกครั้ง", emptyMessage = "ไม่พบข้อมูล" }) => {
  // Data state
  const [dataInside, setDataInside] = useState(data);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);

  // Window states
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Selection states
  const selectAllCheckboxRef = useRef(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Filter states
  const [filterPopoverOpen, setFilterPopoverOpen] = useState({});
  const [filterValues, setFilterValues] = useState({});
  const [tempFilterValues, setTempFilterValues] = useState([]);
  const selectAllFilterRefs = useRef({});

  // Search states
  const [searchPopoverOpen, setSearchPopoverOpen] = useState({});
  const [searchValues, setSearchValues] = useState({});
  const [tempSearchValue, setTempSearchValue] = useState("");

  // Init Data
  useEffect(() => {
    setDataInside(data);
  }, [data]);

  // Init Selected
  useEffect(() => {
    if (initSelectedRows && initSelectedRows.length > 0) {
      setSelectedRows(initSelectedRows);
      if (initSelectedRows.length === dataInside.length) {
        setSelectAll(true);
      }
    }
  }, []);

  // Init Resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Responsive columns (show or hide based on window width)
  const shouldShowColumn = (column) => {
    if (!column.responsive) return true;

    const breakpoint = BREAKPOINTS[column.responsive];
    return windowWidth >= breakpoint;
  };

  // Visible columns filter
  const visibleColumns = columns.filter(shouldShowColumn);

  // Sort states
  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: "asc",
  });

  // Sort handlers
  const handleSort = (column) => {
    let direction = "asc";
    if (sortConfig.column === column) {
      direction = sortConfig.direction === "asc" ? "desc" : null;
    }
    const newSortConfig = direction ? { column, direction } : { column: null, direction: null };

    setSortConfig(newSortConfig);
    if (onSort) {
      onSort(direction ? { column, direction } : { column: null, direction: null });
    }
  };

  // Filter handlers
  const toggleFilterPopover = (column) => {
    setFilterPopoverOpen((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
    setTempFilterValues(filterValues[column] || []);
  };

  const handleFilterChange = (column, value, checked) => {
    if (checked) {
      setTempFilterValues((prev) => [...prev, value]);
    } else {
      setTempFilterValues((prev) => prev.filter((v) => v !== value));
    }
  };

  const handleFilterApply = (column) => {
    const newFilterValues = { ...filterValues, [column]: tempFilterValues };
    setFilterValues(newFilterValues);
    setFilterPopoverOpen((prev) => ({ ...prev, [column]: false }));
    if (onFilter) {
      onFilter(newFilterValues);
    }
  };

  const handleFilterCancel = (column) => {
    setFilterPopoverOpen((prev) => ({ ...prev, [column]: false }));
  };

  useEffect(() => {
    Object.keys(filterPopoverOpen).forEach((column) => {
      const checkbox = selectAllFilterRefs.current[column];
      if (checkbox) {
        const columnOptions = columns.find((col) => col.accessor === column)?.filterOptions || [];
        if (tempFilterValues.length === 0) {
          checkbox.checked = false;
          checkbox.indeterminate = false;
        } else if (tempFilterValues.length === columnOptions.length) {
          checkbox.checked = true;
          checkbox.indeterminate = false;
          setSelectAll(true);
        } else {
          checkbox.checked = false;
          checkbox.indeterminate = true;
          setSelectAll(false);
        }
      }
    });
  }, [tempFilterValues, filterPopoverOpen, columns]);

  // Search handlers
  const toggleSearchPopover = (column) => {
    setSearchPopoverOpen((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
    setTempSearchValue(searchValues[column] || "");
  };

  const handleSearchApply = (column) => {
    const newSearchValues = { ...searchValues, [column]: tempSearchValue };
    setSearchValues(newSearchValues);
    setSearchPopoverOpen((prev) => ({ ...prev, [column]: false }));
    if (onSearch) {
      onSearch(newSearchValues);
    }
  };

  const handleSearchCancel = (column) => {
    setSearchPopoverOpen((prev) => ({ ...prev, [column]: false }));
  };

  // Calculate pagination
  const isServerSide = totalItems !== undefined;
  const actualTotalItems = isServerSide ? totalItems : dataInside.length;
  const totalPages = Math.ceil(actualTotalItems / itemsPerPage);

  // if server-side จะไม่ slice ข้อมูล เพราะ parent จะส่งข้อมูลมาเฉพาะหน้านั้นๆ อยู่แล้ว
  const currentItems = isServerSide ? dataInside : dataInside.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // useEffect call parent if page change
  useEffect(() => {
    if (onPageChange) {
      onPageChange({
        page: currentPage,
        itemsPerPage,
      });
    }
  }, [currentPage, itemsPerPage]);

  // Handle select all checkbox
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]);
      if (onSelectionChange) {
        onSelectionChange([]);
      }
      setSelectAll(false);
    } else {
      const allIds = dataInside.map((item) => item[keyField]);
      setSelectedRows(allIds);
      if (onSelectionChange) {
        const selectedItems = dataInside.filter((item) => allIds.includes(item[keyField]));
        onSelectionChange(selectedItems);
      }
      setSelectAll(true);
    }
  };

  // Handle individual row selection
  const handleSelectRow = (id) => {
    setSelectedRows((prev) => {
      const newSelectedRows = prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id];

      if (onSelectionChange) {
        const selectedItems = dataInside.filter((item) => newSelectedRows.includes(item[keyField]));
        onSelectionChange(selectedItems);
      }

      return newSelectedRows;
    });
  };

  // Update selectAll state and indeterminate state
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      const checkbox = selectAllCheckboxRef.current;

      if (selectedRows.length === 0) {
        checkbox.checked = false;
        checkbox.indeterminate = false;
      } else if (selectedRows.length === dataInside.length) {
        checkbox.checked = true;
        checkbox.indeterminate = false;
        setSelectAll(true);
      } else {
        checkbox.checked = false;
        checkbox.indeterminate = true;
        setSelectAll(false);
      }
    }
  }, [selectedRows, dataInside]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = dataInside.findIndex((item) => (item[keyField]?.toString() || "") === active.id);
      const newIndex = dataInside.findIndex((item) => (item[keyField]?.toString() || "") === over.id);

      const newItems = arrayMove(dataInside, oldIndex, newIndex);
      // อัพเดท state ภายใน
      setDataInside(newItems);
      // ส่งข้อมูลใหม่และข้อมูลการย้ายตำแหน่งไปให้ parent
      if (onSortedChange) {
        onSortedChange({
          items: newItems,
          movement: {
            itemId: dataInside[oldIndex][keyField],
            oldIndex,
            newIndex,
            movedItem: dataInside[oldIndex],
          },
        });
      }
    }
  };

  const renderTableBody = () => {
    // Handle different status states
    if (status === "loading") {
      return <TableShimmer rows={10} columns={visibleColumns.length + (selectable ? 1 : 0)} />;
    }

    if (status === "error") {
      return <TableMessage message={errorMessage} type="error" colSpan={visibleColumns.length + (selectable ? 1 : 0)} />;
    }

    if (status === "empty" || (data.length === 0 && status === "normal")) {
      return <TableMessage message={emptyMessage} type="empty" colSpan={visibleColumns.length + (selectable ? 1 : 0)} />;
    }

    // Normal state (existing code)
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentData = dataInside.slice(startIndex, endIndex);

    if (!sortable) {
      return (
        <tbody style={{ maxHeight: maxHeight }}>
          {currentItems.map((item, rowIndex) => (
            <tr
              key={item[keyField] ?? `row-${rowIndex}`}
              onClick={(event) => {
                if (event.target.tagName !== "INPUT") {
                  onRowClick && onRowClick(item);
                }
              }}
              style={{ cursor: onRowClick ? "pointer" : "default" }}
            >
              {selectable && (
                <td className="checkbox-column b">
                  <div className="d-flex justify-content-center">
                    <Input type="checkbox" checked={selectedRows.includes(item[keyField])} onChange={() => {}} onClick={() => handleSelectRow(item[keyField])} className="form-check-input" />
                  </div>
                </td>
              )}
              {visibleColumns.map((column, index) => {
                return (
                  <td
                    key={index}
                    style={{
                      textAlign: column.align || "start",
                      width: column.width,
                    }}
                  >
                    {column.render ? column.render(item, index, currentPage, itemsPerPage) : get(item, column.accessor)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      );
    }

    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={currentData.map((item) => item[keyField]?.toString() || "")} strategy={verticalListSortingStrategy}>
          <tbody>
            {currentData.map((row, index) => (
              <SortableTableRow key={row[keyField] || index} row={row} index={index} columns={columns} selectable={selectable} selectedRows={selectedRows} handleSelectRow={handleSelectRow} keyField={keyField} />
            ))}
          </tbody>
        </SortableContext>
      </DndContext>
    );
  };

  return (
    <div className="table-responsive">
      <Table hover={onRowClick ? true : false} responsive className={`custom-table ${className} ${maxHeight > 0 ? "fixed-headers" : ""} ${status !== "normal" ? `table-${status}` : ""}`}>
        <thead className="sticky">
          <tr>
            {selectable && (
              <th className="checkbox-column a">
                <div className="d-flex justify-content-center">
                  <Input type="checkbox" innerRef={selectAllCheckboxRef} onChange={handleSelectAll} className="form-check-input" disabled={status !== "normal" || data.length === 0} />
                </div>
              </th>
            )}
            {visibleColumns.map((column, colIndex) => {
              if (column.accessor === "selection") {
                return (
                  <th key={`header-${colIndex}`} className="text-center">
                    <div className="d-flex justify-content-center">
                      <Input type="checkbox" innerRef={selectAllCheckboxRef} onChange={handleSelectAll} className="form-check-input" disabled={status !== "normal" || data.length === 0} />
                    </div>
                  </th>
                );
              }
              return (
                <th
                  key={colIndex}
                  style={{
                    textAlign: column.align || "start",
                    width: column.width,
                  }}
                >
                  <div
                    className="d-flex align-items-center gap-2"
                    style={{
                      height: "24px",
                      justifyContent: column.align || "start",
                    }}
                  >
                    {colIndex === 0 && selectedRows.length > 0 && selectionActions ? (
                      <>
                        {selectionActions}
                        <span className="ms-3">{column.header}</span>
                      </>
                    ) : (
                      <span>{column.header}</span>
                    )}
                    {(column.sortable || column.filterable || column.searchable) && status === "normal" ? (
                      <div className="d-flex align-items-center gap-1 pt-1">
                        {/* Sort Icon */}
                        {column.sortable && (
                          <span className={`cursor-pointer ${sortConfig.column === column.accessor ? "text-primary-500" : "text-gray-600"}`} onClick={() => handleSort(column.accessor)}>
                            {sortConfig.column === column.accessor ? sortConfig.direction === "asc" ? <i className="bx bx-up-arrow-alt" /> : <i className="bx bx-down-arrow-alt" /> : <i className="bx bx-down-arrow-alt" />}
                          </span>
                        )}

                        {/* Filter Icon and Popover */}
                        {column.filterable && column.filterOptions && (
                          <div>
                            <span className={`cursor-pointer ${filterValues[column.accessor]?.length ? "text-primary-500" : "text-gray-600"}`} id={getSafeId("filter", column.accessor)} onClick={() => toggleFilterPopover(column.accessor)}>
                              <i className="bx bx-filter-alt" />
                            </span>
                            <Popover placement="bottom-end" isOpen={filterPopoverOpen[column.accessor]} target={getSafeId("filter", column.accessor)} toggle={() => toggleFilterPopover(column.accessor)}>
                              <PopoverBody>
                                <FormGroup check>
                                  <Label check>
                                    <Input type="checkbox" innerRef={(ref) => (selectAllFilterRefs.current[column.accessor] = ref)} onChange={(e) => (e.target.checked ? setTempFilterValues([...column.filterOptions]) : setTempFilterValues([]))} />
                                    ทั้งหมด
                                  </Label>
                                </FormGroup>
                                <hr />
                                {column.filterOptions.map((option) => (
                                  <FormGroup check key={option}>
                                    <Label check>
                                      <Input type="checkbox" checked={tempFilterValues.includes(option)} onChange={(e) => handleFilterChange(column.accessor, option, e.target.checked)} />
                                      {option}
                                    </Label>
                                  </FormGroup>
                                ))}
                                <hr />
                                <div className="d-flex justify-content-end gap-2">
                                  <Button color="light" size="sm" outline onClick={() => handleFilterCancel(column.accessor)}>
                                    ยกเลิก
                                  </Button>
                                  <Button color="primary" size="sm" outline onClick={() => handleFilterApply(column.accessor)}>
                                    ตกลง
                                  </Button>
                                </div>
                              </PopoverBody>
                            </Popover>
                          </div>
                        )}

                        {/* Search Icon and Popover */}
                        {column.searchable && (
                          <div>
                            <span className={`cursor-pointer ${searchValues[column.accessor] ? "text-primary-500" : "text-gray-600"}`} id={getSafeId("search", column.accessor)} onClick={() => toggleSearchPopover(column.accessor)}>
                              <i className="bx bx-search" />
                            </span>
                            <Popover placement="bottom-end" isOpen={searchPopoverOpen[column.accessor]} target={getSafeId("search", column.accessor)} toggle={() => toggleSearchPopover(column.accessor)}>
                              <PopoverBody>
                                <Input type="text" bsSize="sm" placeholder="ค้นหา..." value={tempSearchValue} onChange={(e) => setTempSearchValue(e.target.value)} />
                                <hr />
                                <div className="d-flex justify-content-end gap-2">
                                  <Button color="light" size="sm" outline onClick={() => handleSearchCancel(column.accessor)}>
                                    ยกเลิก
                                  </Button>
                                  <Button color="primary" size="sm" outline onClick={() => handleSearchApply(column.accessor)}>
                                    ตกลง
                                  </Button>
                                </div>
                              </PopoverBody>
                            </Popover>
                          </div>
                        )}
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        {renderTableBody()}
        {displayFooter && status === "normal" && (
          <tfoot>
            <tr>
              <td colSpan={visibleColumns.length + (selectable ? 1 : 0)}>
                <Pagination currentPage={currentPage} totalPages={totalPages} setCurrentPage={setCurrentPage} />
              </td>
            </tr>
          </tfoot>
        )}
      </Table>
    </div>
  );
};

CustomTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string.isRequired,
      accessor: PropTypes.string.isRequired,
      render: PropTypes.func,
      sortable: PropTypes.bool,
      filterable: PropTypes.bool,
      filterOptions: PropTypes.arrayOf(PropTypes.string),
      searchable: PropTypes.bool,
      responsive: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl", "xxl"]),
      align: PropTypes.oneOf(["start", "center", "end"]),
      width: PropTypes.oneOfType([
        PropTypes.number, // for pixel values
        PropTypes.string, // for percentage values like "20%"
      ]),
    })
  ).isRequired,
  maxHeight: PropTypes.number,
  selectable: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  selectionActions: PropTypes.node,
  sortable: PropTypes.bool,
  onSortedChange: PropTypes.func,
  itemsPerPage: PropTypes.number,
  className: PropTypes.string,
  onRowClick: PropTypes.func,
  onSort: PropTypes.func,
  onFilter: PropTypes.func,
  onSearch: PropTypes.func,
  totalItems: PropTypes.number,
  onPageChange: PropTypes.func,
  displayFooter: PropTypes.bool,
  initSelectedRows: PropTypes.arrayOf(PropTypes.string),
  keyField: PropTypes.string,
  status: PropTypes.oneOf(["normal", "loading", "error", "empty"]),
  errorMessage: PropTypes.string,
  emptyMessage: PropTypes.string,
};

export default CustomTable;
